import { config } from '@fortawesome/fontawesome-svg-core';
import { CookiesProvider } from 'react-cookie';
import { AppProps } from 'next/app';

import BaseLayout from '~/layouts/BaseLayout';
import '@fortawesome/fontawesome-svg-core/styles.css';
import '~/styles/globals.scss';
import { SWRConfig } from 'swr';
import { fetchJson } from '~/lib/fetch';
import { EventEmitter } from '~/lib/events';
import { ReactNode, useEffect } from 'react';
import Script from 'next/script';
import useGtm from '~/lib/useGtm';
import { Link } from '~/components/global';
import { DoradoProvider } from '@appsumo/dorado-react';
import { useRouter } from 'next/router';
import { getFontFamilyClass } from '~/layouts/util';
import { DefaultSeo } from '~/components/seo';
import useGtag from '~/lib/useGtag';

if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    if (args.length === 2) {
      if (args[1]?.credentials === 'same-origin') {
        const options = { ...args[1] };
        delete options.credentials;
        options.mode = 'cors';
        return originalFetch.call(window, args[0], options);
      }
    }
    return originalFetch.call(window, ...args);
  };
}

config.autoAddCss = false;

// this is a monkey-patch to intercept the _next/data request and prefix the CDN url
const useInterceptNextDataHref = ({ router }: { router: any }) => {
  useEffect(() => {
    if (
      process.env.NODE_ENV === 'production' &&
      router.pageLoader?.getDataHref
    ) {
      const originalGetDataHref = router.pageLoader.getDataHref;
      router.pageLoader.getDataHref = function (...args: any[]) {
        // call the originalgetDataHref
        const r = originalGetDataHref.call(router.pageLoader, ...args);
        if (r && r.startsWith('/_next/data/')) {
          // if its a request to _next/data, prefix the CDN url
          //console.log('GOT IT HERE:', r);
          // TODO: replace CDN url from an environment variable
          // return `https://stage-cdn-${process.env.ENV_NAME}.appsumo.com${r}`;
        }
        return r;
      };
    }
  }, [router]);
};

interface CustomAppProps extends Omit<AppProps, 'Component'> {
  Component: AppProps['Component'] & {
    getLayout(page: React.ReactNode): React.ReactElement;
  };
}

const MyApp = ({ Component, pageProps }: CustomAppProps) => {
  const router = useRouter();
  const { trackEvent } = useGtm();
  const { gtag } = useGtag();

  useInterceptNextDataHref({
    router,
  });

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') {
      const handleRouteChange = (url: string) => {
        gtag('event', 'nextRoute', {
          pageType: 'PageView',
          pageTitle: document.title,
          pageUrl: url,
          pageLocation: window.location.href,
          pagePath: url,
          routeName: router.route || '',
        });
      };

      router.events.on('routeChangeComplete', handleRouteChange);

      return () => {
        router.events.off('routeChangeComplete', handleRouteChange);
      };
    }
  }, [router, trackEvent]);

  function getLayout(page: ReactNode) {
    if (Component.getLayout) {
      return Component.getLayout(page);
    }

    return (
      <BaseLayout
        className={`${getFontFamilyClass()} flex min-h-screen flex-col font-sans`}
      >
        {page}
      </BaseLayout>
    );
  }

  return (
    <>
      <Script id="google-tag-manager" strategy="afterInteractive">
        {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
        var f=d.getElementsByTagName(s)[0], j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
        j.async=true;j.src= 'https://www.googletagmanager.com/gtm.js?id='+i+dl;
        f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-K87H2BB');
      `}
      </Script>
      <Script id="gtag-setup" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());
        `}
      </Script>
      <SWRConfig
        value={{
          fetcher: fetchJson,
        }}
      >
        <EventEmitter>
          <CookiesProvider>
            <DoradoProvider value={{ linkComponent: Link as any }}>
              <DefaultSeo />
              {getLayout(<Component {...pageProps} />)}
            </DoradoProvider>
          </CookiesProvider>
        </EventEmitter>
      </SWRConfig>
    </>
  );
};

export default MyApp;
